import React, { useRef, useLayoutEffect, useState } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { useServicesContext, useStockContext, useClientServerUtils } from '@oneaudi/vtp-shared';
import {
  CodeAndIconWrapper,
  NextBestActionsButtonContainer,
  PopoverContentWrapper,
  TextWrapper,
} from './NextBestActions.styles';
import { Text, TextLink, Popover, Headline, IconButton } from '@oneaudi/unified-web-components';
import { trackAudiCodeClick } from '../tracking/Tracking';
import { SYS_SPACE_FIXED_100 } from '@oneaudi/unified-web-common';

export const AudiCodeButton: React.FC = () => {
  const { vehicle } = useStockContext();

  const { getAdditionalService } = useServicesContext();
  const trackingService = getAdditionalService('audi-tracking-service') as TrackingServiceV2;
  const { isServer } = useClientServerUtils();

  const labelAudiCodeButton = useI18n({
    defaultMessage: 'Show Audi code',
    id: 'stockcars.button.audi.code.label',
  });
  const ariaLabelAudiCodeButton = useI18n({
    defaultMessage: 'Click to show Audi code',
    id: 'stockcars.button.audi.code.arialabel',
  });
  const descriptionAudiCodeButton = useI18n({
    defaultMessage: '',
    id: 'stockcars.button.audi.code.description',
  });
  const headlinePopover = useI18n({
    defaultMessage: '',
    id: 'stockcars.popover.audi.code.headline',
  });
  const copyPopover = useI18n({
    defaultMessage: '',
    id: 'stockcars.popover.audi.code.copy',
  });
  const ariaLabelCopyButton = useI18n({
    defaultMessage: '',
    id: 'stockcars.popover.copy.button.arialabel',
  });

  const copyAudiCode = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const [isOpen, setIsOpen] = useState(false);
  const codeAndIconRef = useRef<any>(null);
  const [codeAndIconWidth, setCodeAndIconWidth] = useState(null);

  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      if (codeAndIconRef.current) {
        setCodeAndIconWidth(codeAndIconRef.current.offsetWidth + 48);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [isOpen]);

  if (!vehicle) {
    return null;
  }
  const audiCode = vehicle.basic?.audiCode;

  return (
    <NextBestActionsButtonContainer>
      {isServer ? (
        <TextLink
          variant="standalone"
          icon="code"
          onClick={() => {
            setIsOpen(true);
            trackAudiCodeClick(trackingService, labelAudiCodeButton, audiCode);
          }}
          iconPosition="leading"
          size="md"
          aria-label={ariaLabelAudiCodeButton}
          margin={[0, 0, SYS_SPACE_FIXED_100, 0]}
        >
          {labelAudiCodeButton}
        </TextLink>
      ) : (
        <Popover
          content={
            <PopoverContentWrapper codeAndIconWidth={codeAndIconWidth}>
              <Headline
                tag="h4"
                variant={{ order: '4', type: 'headline', weight: 'normal' }}
                margin={[0, 0, SYS_SPACE_FIXED_100, 0]}
              >
                {headlinePopover}
              </Headline>
              <CodeAndIconWrapper>
                <Headline
                  ref={codeAndIconRef}
                  tag="h1"
                  variant={{ order: '1', type: 'headline', weight: 'bold' }}
                >
                  {audiCode}
                </Headline>
                <IconButton
                  aria-label={ariaLabelCopyButton}
                  icon="copy"
                  size="sm"
                  variant="tertiary"
                  onClick={() => {
                    setIsOpen(false);
                    copyAudiCode(audiCode);
                  }}
                />
              </CodeAndIconWrapper>
              <Text variant={{ order: '1', style: 'normal' }}>{copyPopover}</Text>
            </PopoverContentWrapper>
          }
          id="audi-code-button-popover"
          open={isOpen}
          onClose={() => {
            setIsOpen(false);
          }}
          triggerElement={
            <TextLink
              variant="standalone"
              icon="code"
              onClick={() => {
                setIsOpen(true);
                trackAudiCodeClick(trackingService, labelAudiCodeButton, audiCode);
              }}
              iconPosition="leading"
              size="md"
              aria-label={ariaLabelAudiCodeButton}
              margin={[0, 0, SYS_SPACE_FIXED_100, 0]}
            >
              {labelAudiCodeButton}
            </TextLink>
          }
          withTip
        />
      )}
      <TextWrapper>
        <Text
          variant={{
            type: 'copy',
            order: '2',
            style: 'normal',
          }}
          tag="p"
        >
          {descriptionAudiCodeButton}
        </Text>
      </TextWrapper>
    </NextBestActionsButtonContainer>
  );
};
